import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import { ArrowLeftIcon, PencilSquareIcon, CheckCircleIcon, XMarkIcon, PlusIcon, TrashIcon, KeyIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";

const T = {
  ink:        "#1a1a1c",
  ink2:       "#4a4a50",
  ink3:       "#86868b",
  ink4:       "#b4b3b0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r:          18,
};

const cardStyle = {
  background: T.surface,
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${T.hair}`,
  borderRadius: T.r,
  boxShadow: T.shadowMd,
};

const inputStyle = {
  width: "100%",
  padding: "9px 13px",
  borderRadius: 10,
  border: `1px solid ${T.hair}`,
  background: T.surface2,
  fontSize: 13.5,
  color: T.ink,
  fontFamily: T.font,
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  color: T.ink3,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 500,
  marginBottom: 5,
  display: "block",
};

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const isStrongPassword = (v) => /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(String(v));

const AVATAR_PRESETS = ["#1e3a8a","#0f5c2e","#7c2d12","#4c1d95","#b45309","#0f766e","#be185d","#1d4ed8"];
function derivedColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PRESETS[n % AVATAR_PRESETS.length];
}
function avatarInitials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

export default function PartnerEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || `/admin/partners/${id}`;
  const token = useMemo(() => localStorage.getItem("token") || "", []);
  const apiBase = useMemo(() => API_URL.replace(/\/api\/?$/, ""), []);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [form, setForm] = useState({
    company: "", email: "", role: "partner", cities: [],
    phone: "", contactName: "", category: "", benefit: "",
    benefitDescription: "", whatsappPhone: "", whatsappMessage: "", active: true, color: "",
  });
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [officeCities, setOfficeCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [customCity, setCustomCity] = useState("");

  const [showDel, setShowDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdErr, setPwdErr] = useState("");

  const toAbsolutePhoto = (value = "") => {
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `${apiBase}${value.startsWith("/") ? value : `/${value}`}`;
  };

  useEffect(() => {
    if (!photoFile) { setPhotoPreview(""); return; }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  function pickPhoto(file) {
    if (!file) { setPhotoFile(null); return; }
    if (!file.type.startsWith("image/")) { setErr("Envie apenas imagens (JPG ou PNG)."); setPhotoFile(null); return; }
    if (file.size > 2 * 1024 * 1024) { setErr("Imagem muito grande. Tamanho máximo: 2 MB."); setPhotoFile(null); return; }
    setErr("");
    setPhotoFile(file);
  }

  async function load() {
    if (!API_URL || !token || !id) return;
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API_URL}/admin/partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao carregar parceiro");
      const p = data?.partner || {};
      setForm({
        company: p.company || "", email: p.email || "", role: p.role || "partner",
        cities: p.cities || [], phone: p.phone || "", contactName: p.contactName || "",
        category: p.category || "", benefit: p.benefit || "",
        benefitDescription: p.benefitDescription || "",
        whatsappPhone: p.whatsappPhone || "4440048500",
        whatsappMessage: p.whatsappMessage || `Gostaria de saber mais sobre esse benefício da ${p.company || "[Empresa]"} e a Euro Condomínios`,
        active: !!p.active, color: p.color || "",
      });
      setPhotoUrl(toAbsolutePhoto(p.photoUrl));
      setPhotoFile(null); setPhotoPreview("");
    } catch (e) { setErr(e?.message || "Erro ao carregar dados"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    async function loadOfficeCities() {
      try {
        const resp = await fetch(`${API_URL}/office-cities`);
        if (resp.ok) { const data = await resp.json(); setOfficeCities(data.cities || []); }
      } catch (e) { console.error(e); }
      finally { setLoadingCities(false); }
    }
    loadOfficeCities();
  }, []);

  useEffect(() => {
    setForm(f => ({
      ...f,
      whatsappMessage: f.company.trim()
        ? `Gostaria de saber mais sobre esse benefício da ${f.company} e a Euro Condomínios`
        : "Gostaria de saber mais sobre esse benefício da [Empresa] e a Euro Condomínios",
    }));
  }, [form.company]);

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setErr(""); setOk("");
    if (!form.company || !form.email || !form.contactName || form.cities.length === 0 ||
        !form.phone || !form.category || !form.benefit || (!photoFile && !photoUrl) ||
        !form.whatsappPhone || !form.benefitDescription || !form.whatsappMessage) {
      return setErr("Preencha todos os campos obrigatórios.");
    }
    try {
      setLoading(true);
      const r = await fetch(`${API_URL}/admin/partners/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao salvar");
      if (photoFile) await uploadPhoto(id);
      setOk("Dados atualizados com sucesso!");
      setTimeout(() => navigate(returnTo, { replace: true }), 700);
    } catch (e) { setErr(e?.message || "Falha ao salvar"); }
    finally { setLoading(false); }
  }

  async function uploadPhoto(partnerId) {
    if (!photoFile) return;
    setPhotoProcessing(true);
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      const r = await fetch(`${API_URL}/admin/partners/${partnerId}/photo`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao enviar imagem");
      setPhotoUrl(data?.absoluteUrl || toAbsolutePhoto(data?.photoUrl || ""));
      setPhotoFile(null); setPhotoPreview("");
    } finally { setPhotoProcessing(false); }
  }

  async function handleRemovePhoto() {
    if (!photoUrl) return;
    setPhotoProcessing(true);
    try {
      const r = await fetch(`${API_URL}/admin/partners/${id}/photo`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao remover imagem");
      setPhotoUrl(""); setPhotoFile(null); setPhotoPreview("");
    } catch (e) { setErr(e?.message || "Erro ao remover imagem"); }
    finally { setPhotoProcessing(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/admin/partners/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao excluir");
      setShowDel(false);
      navigate("/admin/partners", { replace: true });
    } catch (e) { setErr(e?.message || "Erro ao excluir"); }
    finally { setDeleting(false); }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwdErr("");
    if (!isStrongPassword(pwd)) return setPwdErr("Mín. 8 caracteres, letras e números.");
    setSavingPwd(true);
    try {
      const r = await fetch(`${API_URL}/admin/partners/${id}/password`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ password: pwd }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao trocar senha");
      setShowPwd(false); setPwd(""); setOk("Senha atualizada com sucesso!");
    } catch (e) { setPwdErr(e?.message || "Erro ao trocar senha"); }
    finally { setSavingPwd(false); }
  }

  if (loading && !form.company) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <Spinner animation="border" style={{ color: T.accent }} />
    </div>
  );

  return (
    <form onSubmit={handleSave} noValidate style={{ fontFamily: T.font }}>

      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, fontSize: 13 }}>
        <button type="button" onClick={() => navigate(returnTo)}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", color: T.ink2, cursor: "pointer", padding: "4px 8px", borderRadius: 8, fontFamily: T.font, fontSize: 13 }}>
          <ArrowLeftIcon style={{ width: 13, height: 13 }} /> Parceiros
        </button>
        <span style={{ color: T.ink4 }}>›</span>
        <span style={{ color: T.ink3 }}>Editar parceiro</span>
      </div>

      {/* Page title */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PencilSquareIcon style={{ width: 18, height: 18, color: T.accent }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: "-0.02em", color: T.ink }}>{form.company || "Editar parceiro"}</h1>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 1 }}>{form.email || "Atualize os dados do parceiro"}</div>
          </div>
        </div>
        {/* Active toggle */}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 14px", borderRadius: 10, border: `1px solid ${T.hair}`, background: T.surface, userSelect: "none" }}>
          <div style={{ position: "relative", width: 36, height: 20 }}>
            <input type="checkbox" name="active" checked={!!form.active} onChange={onChange} style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
            <div style={{ position: "absolute", inset: 0, borderRadius: 999, background: form.active ? T.ok : T.ink4, transition: "background 0.2s" }} />
            <div style={{ position: "absolute", top: 2, left: form.active ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: form.active ? T.ok : T.ink3 }}>{form.active ? "Ativo" : "Inativo"}</span>
        </label>
      </div>

      {/* Alerts */}
      {err && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: T.dangerSoft, color: T.danger, fontSize: 13, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <XMarkIcon style={{ width: 15, height: 15, flexShrink: 0 }} />{err}
        </div>
      )}
      {ok && (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: T.okSoft, color: T.ok, fontSize: 13, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleIcon style={{ width: 15, height: 15, flexShrink: 0 }} />{ok}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Conta de acesso */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.hair}`, fontSize: 13, fontWeight: 600, color: T.ink }}>Conta de acesso</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ marginBottom: 16 }}>
              <Field label="Empresa *">
                <input style={inputStyle} name="company" required placeholder="Nome da empresa" value={form.company} onChange={onChange} />
              </Field>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Cor do avatar</label>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:form.color||derivedColor(form.company), color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700, letterSpacing:"-0.02em", flexShrink:0, transition:"background 0.2s" }}>
                  {avatarInitials(form.company||"EP")}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <button type="button" onClick={()=>setForm(f=>({...f,color:""}))} title="Automático (padrão)"
                    style={{ width:28, height:28, borderRadius:"50%", background:derivedColor(form.company), border:!form.color?`3px solid ${T.ink}`:`2px solid transparent`, cursor:"pointer", position:"relative", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"border 0.15s" }}>
                    {!form.color && <span style={{ width:7, height:7, borderRadius:"50%", background:"#fff" }}/>}
                  </button>
                  {AVATAR_PRESETS.map(c => (
                    <button key={c} type="button" onClick={()=>setForm(f=>({...f,color:c}))} title={c}
                      style={{ width:28, height:28, borderRadius:"50%", background:c, border:form.color===c?`3px solid ${T.ink}`:`2px solid transparent`, cursor:"pointer", flexShrink:0, transition:"border 0.15s, transform 0.1s" }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.15)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    />
                  ))}
                  <label title="Cor personalizada" style={{ width:28, height:28, borderRadius:"50%", border:`2px dashed ${T.ink3}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, background: form.color && !AVATAR_PRESETS.includes(form.color) ? form.color : "transparent", position:"relative" }}>
                    <input type="color" value={form.color||"#1e3a8a"} onChange={e=>setForm(f=>({...f,color:e.target.value}))} style={{ opacity:0, position:"absolute", inset:0, cursor:"pointer", width:"100%", height:"100%" }} />
                    {(!form.color || AVATAR_PRESETS.includes(form.color)) && <span style={{ fontSize:16, color:T.ink3, lineHeight:1 }}>+</span>}
                  </label>
                </div>
                {!form.color && <span style={{ fontSize:11.5, color:T.ink3 }}>Automático</span>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 160px", gap: 14 }}>
              <Field label="E-mail *">
                <input style={inputStyle} type="email" name="email" required placeholder="contato@empresa.com" value={form.email} onChange={onChange} />
              </Field>
              <Field label="Perfil">
                <select style={{ ...inputStyle, appearance: "none", WebkitAppearance: "none" }} name="role" value={form.role} onChange={onChange}>
                  <option value="partner">parceiro</option>
                  <option value="admin">admin</option>
                </select>
              </Field>
              <Field label="Status">
                <div style={{ display: "flex", alignItems: "center", height: 38, gap: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 12.5, fontWeight: 500, background: form.active ? T.okSoft : T.hair2, color: form.active ? T.ok : T.ink3 }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: form.active ? T.ok : T.ink3 }} />
                    {form.active ? "Ativo" : "Inativo"}
                  </span>
                </div>
              </Field>
            </div>
          </div>
        </div>

        {/* Informações de contato */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.hair}`, fontSize: 13, fontWeight: 600, color: T.ink }}>Informações de contato</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <Field label="Nome de contato *">
                <input style={inputStyle} name="contactName" required placeholder="Responsável" value={form.contactName} onChange={onChange} />
              </Field>
              <Field label="Telefone *">
                <input style={inputStyle} name="phone" required placeholder="(00) 00000-0000" value={form.phone} onChange={onChange} />
              </Field>
            </div>

            <div style={{ height: 1, background: T.hair, marginBottom: 20 }} />
            <div style={{ fontSize: 11, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500, marginBottom: 12 }}>Cidades onde atende *</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Selecione as cidades onde a empresa atua</div>
                {loadingCities ? (
                  <div style={{ fontSize: 13, color: T.ink3 }}>Carregando cidades…</div>
                ) : (
                  <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "10px 14px", maxHeight: 200, overflowY: "auto", background: T.surface2 }}>
                    {officeCities.map(cityObj => (
                      <label key={cityObj.value} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 13, color: T.ink }}>
                        <input
                          type="checkbox"
                          checked={form.cities.includes(cityObj.value)}
                          onChange={e => {
                            if (e.target.checked) setForm(f => ({ ...f, cities: [...f.cities, cityObj.value] }));
                            else setForm(f => ({ ...f, cities: f.cities.filter(c => c !== cityObj.value) }));
                          }}
                          style={{ accentColor: T.accent, width: 14, height: 14 }}
                        />
                        {cityObj.value}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 8 }}>Adicionar cidade personalizada</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="Ex.: São Paulo-SP"
                    value={customCity}
                    onChange={e => setCustomCity(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = customCity.trim();
                        if (t && !form.cities.includes(t)) { setForm(f => ({ ...f, cities: [...f.cities, t] })); setCustomCity(""); }
                      }
                    }}
                  />
                  <button type="button"
                    onClick={() => {
                      const t = customCity.trim();
                      if (t && !form.cities.includes(t)) { setForm(f => ({ ...f, cities: [...f.cities, t] })); setCustomCity(""); }
                    }}
                    disabled={!customCity.trim()}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.hair}`, background: T.accent, color: "#fff", cursor: customCity.trim() ? "pointer" : "not-allowed", opacity: customCity.trim() ? 1 : 0.4, flexShrink: 0 }}>
                    <PlusIcon style={{ width: 16, height: 16 }} />
                  </button>
                </div>
                <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "10px 14px", minHeight: 80, background: T.surface2 }}>
                  {form.cities.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: T.ink4, textAlign: "center", paddingTop: 16 }}>Nenhuma cidade selecionada</div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {form.cities.map(city => (
                        <span key={city} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: T.accentSoft, color: T.accent, fontSize: 12.5, fontWeight: 500 }}>
                          {city}
                          <button type="button" onClick={() => setForm(f => ({ ...f, cities: f.cities.filter(c => c !== city) }))}
                            style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", color: T.accent, cursor: "pointer", padding: 0 }}>
                            <XMarkIcon style={{ width: 12, height: 12 }} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefício */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.hair}`, fontSize: 13, fontWeight: 600, color: T.ink }}>Benefício</div>
          <div style={{ padding: "20px 20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <Field label="Categoria *">
                <input style={inputStyle} name="category" required placeholder="Ex.: Energia, Segurança, Limpeza…" value={form.category} onChange={onChange} />
              </Field>
              <Field label="Benefício *">
                <input style={inputStyle} name="benefit" required placeholder="Título do benefício" value={form.benefit} onChange={onChange} maxLength={50} />
              </Field>
            </div>
            <Field label="Descrição do benefício *">
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: 90, lineHeight: 1.55 }}
                name="benefitDescription" required
                placeholder="Detalhes exibidos no site e no app"
                value={form.benefitDescription} onChange={onChange} maxLength={1000}
              />
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 4 }}>{form.benefitDescription.length}/1000 caracteres</div>
            </Field>
          </div>
        </div>

        {/* Comunicação digital */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.hair}`, fontSize: 13, fontWeight: 600, color: T.ink }}>Comunicação digital</div>
          <div style={{ padding: "20px 20px 24px", display: "grid", gridTemplateColumns: "200px 1fr", gap: 14 }}>
            <Field label="WhatsApp (só números) *">
              <input style={inputStyle} name="whatsappPhone" required placeholder="5544999999999" value={form.whatsappPhone} onChange={onChange} />
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 4 }}>Com DDD e código do país (55)</div>
            </Field>
            <Field label="Mensagem padrão do WhatsApp *">
              <textarea
                style={{ ...inputStyle, resize: "vertical", minHeight: 60, lineHeight: 1.55 }}
                name="whatsappMessage" required
                placeholder="Mensagem pré-preenchida ao abrir o chat"
                value={form.whatsappMessage} onChange={onChange}
              />
            </Field>
          </div>
        </div>

        {/* Imagem */}
        <div style={cardStyle}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.hair}`, fontSize: 13, fontWeight: 600, color: T.ink }}>Imagem do card</div>
          <div style={{ padding: "20px 20px 24px", display: "grid", gridTemplateColumns: "1fr 220px", gap: 20, alignItems: "start" }}>
            <div>
              <Field label={`Arquivo de imagem${photoUrl ? "" : " *"}`}>
                <label style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:10, border:`1px solid ${T.hair}`, background:T.surface2, cursor:"pointer" }}>
                  <input type="file" accept="image/*" required={!photoUrl} onChange={e => pickPhoto(e.target.files?.[0] || null)} style={{ display:"none" }} />
                  <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:7, background:T.surface, border:`1px solid ${T.hair}`, fontSize:13, fontWeight:500, color:T.ink2, flexShrink:0, whiteSpace:"nowrap" }}>
                    <ArrowUpTrayIcon style={{ width:13, height:13 }}/> Escolher arquivo
                  </span>
                  <span style={{ fontSize:13, color:photoFile ? T.ink : T.ink4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {photoFile ? photoFile.name : photoUrl ? "Imagem atual carregada" : "Nenhum arquivo selecionado"}
                  </span>
                </label>
              </Field>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 8, lineHeight: 1.6 }}>
                Formato JPG ou PNG, até 2 MB.<br />
                Use imagem quadrada (ex.: 300×300 px) para evitar cortes.
              </div>
              {photoUrl && (
                <button type="button" onClick={handleRemovePhoto} disabled={photoProcessing}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, padding: "6px 12px", borderRadius: 8, border: `1px solid ${T.dangerSoft}`, background: T.dangerSoft, color: T.danger, fontSize: 12.5, cursor: photoProcessing ? "not-allowed" : "pointer", fontFamily: T.font, opacity: photoProcessing ? 0.6 : 1 }}>
                  <TrashIcon style={{ width: 13, height: 13 }} />
                  {photoProcessing ? "Removendo…" : "Remover imagem atual"}
                </button>
              )}
            </div>
            <div>
              {(photoPreview || photoUrl) ? (
                <img src={photoPreview || photoUrl} alt="Preview" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 12, border: `1px solid ${T.hair}` }} />
              ) : (
                <div style={{ width: "100%", aspectRatio: "1", borderRadius: 12, border: `1.5px dashed ${T.hair}`, background: T.hair2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: T.ink4 }}>
                  Pré-visualização
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingBottom: 24 }}>
          <button type="submit" disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10, border: "none", background: loading ? T.ink4 : T.accent, color: "#fff", fontSize: 13.5, fontWeight: 500, cursor: loading ? "not-allowed" : "pointer", fontFamily: T.font }}>
            {loading
              ? <><Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> Salvando…</>
              : <><CheckCircleIcon style={{ width: 15, height: 15 }} /> Salvar alterações</>}
          </button>
          <button type="button" onClick={() => navigate(returnTo)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 13.5, cursor: "pointer", fontFamily: T.font }}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => setShowPwd(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
            <KeyIcon style={{ width: 14, height: 14 }} /> Trocar senha
          </button>
          <button type="button" onClick={() => setShowDel(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: `1px solid ${T.dangerSoft}`, background: T.dangerSoft, color: T.danger, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
            <TrashIcon style={{ width: 14, height: 14 }} /> Excluir parceiro
          </button>
        </div>

      </div>

      {/* Modal Excluir */}
      <Modal show={showDel} onHide={() => setShowDel(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "18px 24px" }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 600, color: T.ink, display: "flex", alignItems: "center", gap: 10 }}>
            <TrashIcon style={{ width: 16, height: 16, color: T.danger }} /> Excluir parceiro
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", fontSize: 13.5, color: T.ink2, lineHeight: 1.6 }}>
          <div style={{ padding: "12px 16px", borderRadius: 10, background: T.dangerSoft, color: T.danger, fontSize: 13, marginBottom: 14 }}>
            Esta ação não poderá ser desfeita.
          </div>
          Tem certeza que deseja excluir <strong>{form.company}</strong>? Todos os dados do parceiro serão removidos permanentemente.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "14px 24px", gap: 8 }}>
          <button type="button" onClick={() => setShowDel(false)}
            style={{ padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
            Cancelar
          </button>
          <button type="button" onClick={handleDelete} disabled={deleting}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: "none", background: T.danger, color: "#fff", fontSize: 13, fontWeight: 500, cursor: deleting ? "not-allowed" : "pointer", fontFamily: T.font, opacity: deleting ? 0.7 : 1 }}>
            <TrashIcon style={{ width: 14, height: 14 }} /> {deleting ? "Excluindo…" : "Confirmar exclusão"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal Trocar Senha */}
      <Modal show={showPwd} onHide={() => { setShowPwd(false); setPwd(""); setPwdErr(""); }} centered>
        <form onSubmit={handleChangePassword}>
          <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "18px 24px" }}>
            <Modal.Title style={{ fontSize: 15, fontWeight: 600, color: T.ink, display: "flex", alignItems: "center", gap: 10 }}>
              <KeyIcon style={{ width: 16, height: 16, color: T.accent }} /> Trocar senha
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "20px 24px" }}>
            <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 14, lineHeight: 1.6 }}>
              A senha deve ter <strong>ao menos 8 caracteres</strong> e conter <strong>letras e números</strong>.
            </div>
            <label style={labelStyle}>Nova senha</label>
            <input
              type="password" required minLength={8}
              placeholder="Nova senha"
              value={pwd} onChange={e => setPwd(e.target.value)}
              style={{ ...inputStyle, borderColor: pwdErr ? T.danger : undefined }}
            />
            {pwdErr && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>{pwdErr}</div>}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "14px 24px", gap: 8 }}>
            <button type="button" onClick={() => { setShowPwd(false); setPwd(""); setPwdErr(""); }}
              style={{ padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
              Cancelar
            </button>
            <button type="submit" disabled={savingPwd}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, cursor: savingPwd ? "not-allowed" : "pointer", fontFamily: T.font, opacity: savingPwd ? 0.7 : 1 }}>
              <CheckCircleIcon style={{ width: 14, height: 14 }} /> {savingPwd ? "Salvando…" : "Salvar senha"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

    </form>
  );
}
